import { Component, inject, Input, signal } from '@angular/core';
import { BaseButtonComponent } from '../buttons/base-button';
import { Util } from '../../util';
import { Action, ActionType } from '../../models/action';
import { Checkout } from '../../state/user/checkouts/models/checkout';
import { ShareType } from '../../models/social';
import { Record } from '../../models/record';
import { EditListFacade } from '../../edit-list/state/edit-list.facade';
import { CitationFacade } from '../../_core/state/dialogs/citation/citation.facade';
import { ReportDialogFacade } from 'src/app/_core/state/dialogs/report/report.facade';

@Component({
  selector: 'app-base-dropdown-options-button',
  templateUrl: './base-dropdown-options-button.component.html',
  styleUrls: ['./base-dropdown-options-button.component.scss']
})
export class BaseDropdownOptionsButtonComponent extends BaseButtonComponent {
  protected readonly editListFacade = inject(EditListFacade);
  protected readonly citationFacade = inject(CitationFacade);
  protected readonly reportDialogFacade = inject(ReportDialogFacade);

  protected readonly showReturnCheckoutDialog = signal<boolean>(false);

  @Input() record: Record;

  checkout: Checkout;

  showReturnLoanButton: boolean;
  alreadyInFavorites: boolean = false;
  isCheckedOut: boolean;
  favoriteText: string = 'tooltip.addToFavorites';
  favoriteIcon: 'favorite' | 'favorite_black' = 'favorite';
  showShareButtons: boolean;
  socialButtons: ShareType[];
  shareText: string;
  showReportIssueButton: boolean = false;

  doRenew() {
    if (this.waiting) {
      return;
    }

    this.doAction(() => {
      this.checkoutsFacade.postRenewCheckout(this.checkout).subscribe(
        () => {
          this.renewed.emit(this.checkout);
        },
        (_err) => {
          this.waiting = false;
          throw _err;
        }
      );
    });
  }

  doReturn() {
    if (this.waiting) {
      return;
    }
    this.doAction(() => {
      this.showReturnCheckoutDialog.set(false);
      this.checkoutsFacade.postReturnCheckout(this.checkout.id).subscribe(() => {
        this.toastService.showToastSolution('messages.checkouts.return');

        this.ratingDialogFacade.openDialog(this.record);
        this.updateAvailability();
      });
    });
  }

  toggleFavorite() {
    if (this.alreadyInFavorites) {
      this.removeFromFavorites();
    } else {
      this.addToFavorites();
    }
  }

  getSharingUrl(recordId: string) {
    return Util.getOrigin() + '/info/' + recordId;
  }

  openNewListDialog() {
    const action: Action = {
      type: ActionType.ADD_TO_LIST,
      recordId: this.record.id,
    };

    this.doAction(() => {
      this.editListFacade.openDialog(undefined, this.record);
      return;
    }, action);
  }

  isRecordInList(listId: number, recordId: string) {
    return this.userService.isInList(listId, recordId);
  }

  addToList(listId: number) {
    if (this.userService.isInList(listId, this.record.id)) {
      return this.toastService.showToast('messages.lists.duped', 'info');
    }

    this.apiService.addToList(this.userService.getUserId(), listId, this.record.id).subscribe((item) => {
      this.userService.lists.find((list) => list.id === item.listId).items.unshift(item);
      this.toastService.showToast('messages.lists.add');
    });
  }

  openCitationDialog() {
    this.doAction(() => {
      this.citationFacade.openCitationModal(this.record);
    });
  }

  openReportDialog() {
    this.doAction(() => {
      this.reportDialogFacade.openDialog();
    });
  }

  addToFavorites() {
    const action: Action = {
      type: ActionType.ADD_FAVORITE,
      recordId: this.record.id,
    };

    this.doAction(() => {
      this.favoriteFacade.postFavorite(this.record.id).subscribe(() => { this.waiting = false; this.alreadyInFavorites = true });
    }, action);
  }

  removeFromFavorites() {
    const favorite = this.favoriteFacade.getFavoriteByRecordId(this.record.id);
    this.favoriteFacade.deleteFavorite(favorite.recordId);
  }

  protected checkIsCheckedOut() {
    if (this.record.containerData !== null && this.isParentContainer()) {
      return false;
    }

    return this.checkout !== null;
  }
}
